import {
  ETypeMainHead,
  CTypeCardView,
  FUNERAL_SIDO,
  DTypeBreadCrumb,
  FUNERAL_SIGUNGU,
  ATypeBeneficiaryCTA,
  colors,
  BTypeMap,
  FullWidthButton,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import facilities from "goi_common/src/constants/facilities_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((f) => targetSidos.includes(f.sido) && f.is_public)
    .map((f) => ({
      params: {
        companyname: f.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname;
  let p_facility;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    p_facility = facilities.find((f) => f.companyname === companyname);
    if (p_facility) {
      name = p_facility.companyname;

      district = p_facility.address.slice(0, 2);
      if (p_facility.address.split(" ")[0].length == 4)
        district = p_facility.address[0] + p_facility.address[2];

      description = `${
        p_facility?.companyname || ""
      }의 장지 이용료를 감면 받기 위하여 기초생활수급자임을 증명하는 서류가 필요합니다. 수급자 증명서는 전국 시군구 또는 읍면동 주민센터에서 발급 가능하며, 무인민원발급기(본인에 한함)와 정부24 웹사이트에서 또한 발급 가능합니다.`;
    }
  }

  return {
    props: {
      defaultPFacility: p_facility,
      pageDescription: description,
      pageKeywords: `${p_facility?.companyname}, ${p_facility?.companyname} 기초생활수급자, ${district} 기초생활수급자 장례, 장제비, 장제급여, 기초생활수급자 장례 얼마,`,
      pageOgTitle: `${p_facility?.companyname} 기초생활 보호 대상자를 위한 공설 장지 비용 안내 | 서울경기인천장례정보포털`,
      pageOgDescription: description,
      pageOgImage: p_facility?.images[0].url || "",
      hasError: !p_facility,
    },
  };
};

export default function companyname({
  defaultPFacility,
  pageOgTitle,
}: {
  defaultPFacility: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionPFacilities = facilities.filter((p) =>
    targetSidos.includes(String(p.sido)) && p.is_public
  );

  const carouselItems = regionPFacilities.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.metropolitan-funeral.co.kr/benefit/jangji/${res.companyname}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionPFacilities.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionPFacilities.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <DTypeBreadCrumb
        mainColor={regionColor}
        firstItem={{
          text: `수도권 기초생활수급자 장례헬프센터`,
          href: `/beneficiary/`,
          event: {
            domain: "metro",
            path: "benef_facility",
            trigger_and_target: "br_crumb_benef",
          },
        }}
        goiHomeEvent={{
          domain: "metro",
          path: "benef_facility",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ETypeMainHead
        mainColor={regionColor}
        defaultParlor={defaultPFacility}
        title={`${defaultPFacility.companyname}`}
        managed_type={defaultPFacility.managed_type}
        address={defaultPFacility.address}
        isJangji
        event={{
          domain: "metro",
          path: "benef_facility",
          trigger_and_target: "uv_mth_req",
        }}
      />

      <Stack
        className="body_16_m"
        style={{
          background: colors.gray50,
          padding: "12px 14px",
          margin: "0 20px",
        }}
      >
        기초생활수급자는 화장 비용이 전액 감면됩니다.
      </Stack>

      <Stack padding="18px 20px 40px" gap="40px">
        <Box className="body_14_m">
          e-하늘장사정보시스템에서 가져온 정보이며, 자세한 안치 대상 여부를
          알아보려면 상담이 필요합니다.
        </Box>{" "}
        <Box className="subtitle_18_b">필요 서류</Box>
        <Stack gap="12px">
          <Box className="body_14_m" component="p">
            화장 비용을 전액 감면 받기 위하여 기초생활수급자임을 증명하는 서류가
            필요합니다.
          </Box>
          <Box className="subtitle_14_b" component="p">
            필요 서류 : 국민기초생활보장수급자 증명서
          </Box>
          <Box className="body_14_m" component="p">
            수급자 증명서는 전국 시군구 또는 읍면동 주민센터에서 발급 가능하며,
            무인민원발급기(본인에 한함)와 정부24 웹사이트에서 또한 발급
            가능합니다.
          </Box>
          <Box className="body_14_m" component="p">
            기초생활수급자라면 위의 2가지 혜택을 받을 수 있습니다.{" "}
            <b className="subtitle_14_b">
              이해하기 어려운 점이 있거나 더 자세하게 알고 싶은 것이 있다면,
              바로 상담을 진행하세요.
            </b>
          </Box>
        </Stack>
        <FullWidthButton
          text="바로 상담하기"
          href="tel:1666-9784"
          backgroundColor={regionColor.c300}
          event={{
            domain: "metro",
            path: "benef_facility",
            trigger_and_target: "goi_call",
          }}
        />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <BTypeMap defaultParlor={defaultPFacility} />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack padding="0px 20px" marginBottom="40px" gap="8px">
        <h3 className="subtitle_18_b" style={{ textAlign: "center" }}>
          서울 · 경기 · 인천 지역 장지
        </h3>
        {regionPFacilities.map((res) => (
          <CTypeCardView
            sigungu={res.sigungu}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/benefit/jangji/${res.companyname}/`}
            // descriptions={["기초생활수급자 무료"]}
            descriptions={[""]}
            sido={String(res.sido)}
            imgSrc={res.images[0]?.url ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "benef_facility",
              trigger_and_target: "uv_fr_facility_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
